/**
 * Accounts data layer (FR-ACC-01 to FR-ACC-10, FR-CARD-05).
 *
 * Every mutation that changes an existing record writes an audit entry alongside it
 * (FR-AUD-01). Creates do not — `createdAt` and `enteredBy` already cover them.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import type { Account, AccountType, LimitChange } from '../core/accounts';
import { canDelete } from '../core/accounts';
import { buildSeedAccounts } from '../core/seed';
import type { DateKey } from '../core/dates';
import { todayKey } from '../core/dates';
import type { Paise } from '../core/money';
import { auditDelete, auditUpdate, writeAudit } from './audit';
import { db } from './firebase';
import { releaseSource, reportSnapshotMetadata, trackWrite } from './syncState';

const COLLECTION = 'accounts';

export class AccountError extends Error {}

function toAccount(id: string, data: Record<string, unknown>): Account {
  return {
    id,
    name: (data.name as string) ?? id,
    type: (data.type as AccountType) ?? 'bank',
    currency: (data.currency as 'INR' | 'USD') ?? 'INR',
    archived: data.archived === true,
    openingBalancePaise: (data.openingBalancePaise as number) ?? 0,
    openingBalanceDate: (data.openingBalanceDate as DateKey) ?? todayKey(),
    balancePaise: (data.balancePaise as number) ?? (data.openingBalancePaise as number) ?? 0,
    historicalOpeningPaise: (data.historicalOpeningPaise as number) ?? null,
    minBalancePaise: (data.minBalancePaise as number) ?? null,
    transactionCount: (data.transactionCount as number) ?? 0,
    sortOrder: (data.sortOrder as number) ?? 0,
    creditLimitPaise: data.creditLimitPaise as number | undefined,
    statementDay: (data.statementDay as number | null) ?? null,
    dueDay: data.dueDay as number | undefined,
    dueDayIsNextMonth: data.dueDayIsNextMonth as boolean | undefined,
    loanId: data.loanId as string | undefined,
    note: (data.note as string) ?? null,
  };
}

export function watchAccounts(onChange: (accounts: Account[]) => void): () => void {
  const unsubscribe = onSnapshot(
    query(collection(db, COLLECTION), orderBy('sortOrder')),
    { includeMetadataChanges: true },
    (snapshot) => {
      reportSnapshotMetadata('accounts', snapshot.metadata);
      onChange(snapshot.docs.map((d) => toAccount(d.id, d.data())));
    },
    () => onChange([]),
  );

  return () => {
    releaseSource('accounts');
    unsubscribe();
  };
}

// ---------------------------------------------------------------------------
// Seeding
// ---------------------------------------------------------------------------

/**
 * Write the 32 seeded accounts (SCHEMA §10).
 *
 * Refuses to run if any account exists. Re-seeding over live data would reset opening
 * balances that had since been corrected, and a "seed" button is exactly the sort of
 * thing that gets clicked twice.
 */
export async function seedAccounts(by: string): Promise<number> {
  const existing = await getDocs(collection(db, COLLECTION));
  if (!existing.empty) {
    throw new AccountError(
      `Accounts already exist (${existing.size}). Seeding would overwrite them.`,
    );
  }

  const seeds = buildSeedAccounts();
  const batch = writeBatch(db);

  for (const seed of seeds) {
    // The seed `note` is stored, not stripped: it is where "opening position unknown"
    // lives for Jchit and GRT Pay, and why HDFC Current dates from February. Dropping
    // it would leave those figures looking like plain zeroes.
    batch.set(doc(db, COLLECTION, seed.id), {
      ...seed,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      enteredBy: by,
      deleted: false,
    });
  }

  // A card's seeded limit is the first entry in its dated series, not a bare value —
  // otherwise the first real limit change has nothing to supersede and utilisation
  // before that change has no limit to measure against (FR-CARD-05).
  for (const seed of seeds) {
    if (seed.type !== 'credit_card') continue;
    batch.set(doc(collection(db, COLLECTION, seed.id, 'limitHistory')), {
      limitPaise: seed.creditLimitPaise ?? 0,
      effectiveFrom: seed.openingBalanceDate,
      changedBy: by,
      changedAt: serverTimestamp(),
      note: 'Seeded',
    });
  }

  await trackWrite(batch.commit());
  return seeds.length;
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export interface NewAccount {
  name: string;
  type: AccountType;
  openingBalancePaise: Paise;
  openingBalanceDate: DateKey;
  minBalancePaise?: Paise | null;
  creditLimitPaise?: Paise;
  statementDay?: number | null;
  dueDay?: number;
}

/** Slug ids are frozen at creation and never track a rename (SD-03). */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
}

export async function createAccount(input: NewAccount, by: string): Promise<string> {
  const id = slugify(input.name);
  if (!id) throw new AccountError('That name produces an empty id.');

  const existing = await getDoc(doc(db, COLLECTION, id));
  if (existing.exists()) throw new AccountError(`An account with the id "${id}" already exists.`);

  await trackWrite(
    setDoc(doc(db, COLLECTION, id), {
      ...input,
      currency: 'INR',
      archived: false,
      balancePaise: input.openingBalancePaise,
      transactionCount: 0,
      sortOrder: 999,
      historicalOpeningPaise: null,
      deleted: false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      enteredBy: by,
    }),
  );

  if (input.type === 'credit_card') {
    await trackWrite(
      addDoc(collection(db, COLLECTION, id, 'limitHistory'), {
        limitPaise: input.creditLimitPaise ?? 0,
        effectiveFrom: input.openingBalanceDate,
        changedBy: by,
        changedAt: serverTimestamp(),
      }),
    );
  }

  return id;
}

/**
 * Update an account and log what changed.
 *
 * Renaming is retroactive by construction (FR-ACC-05): transactions store the slug id,
 * never the display name, so this one write updates every historical row's label.
 */
export async function updateAccount(
  account: Account,
  patch: Partial<Account>,
  by: string,
): Promise<void> {
  await trackWrite(
    updateDoc(doc(db, COLLECTION, account.id), { ...patch, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(
    COLLECTION,
    account.id,
    account as unknown as Record<string, unknown>,
    { ...account, ...patch } as unknown as Record<string, unknown>,
    by,
  );
}

export async function setArchived(account: Account, archived: boolean, by: string): Promise<void> {
  await updateAccount(account, { archived }, by);
}

/**
 * Hard-delete an account.
 *
 * Only permitted with zero transactions (FR-ACC-03). The check is here for a clear
 * message, and again in the security rules, which are the actual authority — the rules
 * read `transactionCount` for exactly this reason.
 */
export async function deleteAccount(account: Account, by: string): Promise<void> {
  if (!canDelete(account)) {
    throw new AccountError(
      `${account.name} has ${account.transactionCount} transactions. Archive it instead.`,
    );
  }
  await auditDelete(COLLECTION, account.id, account as unknown as Record<string, unknown>, by);
  await trackWrite(deleteDoc(doc(db, COLLECTION, account.id)));
}

// ---------------------------------------------------------------------------
// Credit limits (FR-CARD-05, FR-CARD-06)
// ---------------------------------------------------------------------------

export function watchLimitHistory(
  accountId: string,
  onChange: (history: LimitChange[]) => void,
): () => void {
  return onSnapshot(
    query(collection(db, COLLECTION, accountId, 'limitHistory'), orderBy('effectiveFrom')),
    (snapshot) =>
      onChange(
        snapshot.docs.map((d) => ({
          limitPaise: (d.data().limitPaise as number) ?? 0,
          effectiveFrom: (d.data().effectiveFrom as DateKey) ?? '1970-01-01',
        })),
      ),
    () => onChange([]),
  );
}

/**
 * Record a credit-limit change as a dated entry, never an overwrite.
 *
 * The denormalised `creditLimitPaise` on the parent is updated too, but only as a copy
 * of the latest entry for the common case. Utilisation for a past month reads the
 * series, so raising a limit today cannot rewrite last year's numbers.
 */
export async function changeCreditLimit(
  account: Account,
  limitPaise: Paise,
  effectiveFrom: DateKey,
  by: string,
): Promise<void> {
  if (account.type !== 'credit_card') {
    throw new AccountError('Only credit cards carry a limit.');
  }

  await trackWrite(
    addDoc(collection(db, COLLECTION, account.id, 'limitHistory'), {
      limitPaise,
      effectiveFrom,
      changedBy: by,
      changedAt: serverTimestamp(),
    }),
  );

  await trackWrite(
    updateDoc(doc(db, COLLECTION, account.id), {
      creditLimitPaise: limitPaise,
      updatedAt: serverTimestamp(),
    }),
  );

  await writeAudit({
    action: 'update',
    collection: COLLECTION,
    documentId: account.id,
    changes: [
      {
        field: 'creditLimitPaise',
        before: account.creditLimitPaise ?? null,
        after: limitPaise,
      },
      { field: 'limitEffectiveFrom', before: null, after: effectiveFrom },
    ],
    by,
  });
}
